import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { JSX } from 'react';
import { appContext } from '../../../../AppContext';
import Helper from '../../../../Common/Helper';
import { PagedResponse } from "@iconics/core";
import { IPatchSolutionResponse, IPostSolutionResponse, Ontology, Solution } from './DataModels';
import { ApiConstants } from '../../../../Providers.Api/ApiConstants';
import { IRouterProps, withRouting } from "@iconics/components";
import EditSolutionPackages from './EditSolutionPackages';
import EditSolutionDetailsForm from './EditSolutionDetailsForm';
import EditSolutionParameters from './EditSolutionParameters';
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { Validator } from '@iconics/core';
import { Button, ButtonBar, DeleteButton, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons } from '@iconics/components';
import { TypeHelper } from '@iconics/core';

class EditSolution extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state =
        {
            id: '',
            name: '',
            displayName: this.languageValuesHelper.default(),
            description: '',
            enabled: false,
            showOnHomePage: false,
            showOnMenu: false,
            concurrencyToken: '',
            ontologyId: '',
            nameError: '',
            descriptionError: '',
            ontologyError: '',
            ontologies: [],
            images: [],
            imageUri: '',
            iconUri: '',
            applicationLink: '',
            order: '',
            canDelete: this.local.hasRight('DATAMODEL.Solutions.Delete'),
            sections: [],
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_Solution_PageTitle;
        this.backUrl = '/admin/solutions';
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();
        this.validator
            .addInput('description', () => this.state.description, i => this.setState({ descriptionError: i }))
            .isRequired();
        this.validator
            .addInput('ontologyId', () => this.state.ontologyId, i => this.setState({ ontologyError: i }))
            .isRequired();
            
        if (this.props.params.id)
        {
            await this.loadSolution();
        }
        await this.loadOntologies();
        await this.loadImages();
    }

    public async componentDidUpdate(prevProps: Readonly<IProps>, _: Readonly<IState>): Promise<void>
    {
        if (!prevProps.params.id && this.props.params.id)
        {
            await this.loadSolution();
        }
    }

    private async loadSolution(): Promise<void>
    {
        if (!this.props.params.id)
        {
            return;
        }

        try
        {
            const solutionData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.get<Solution>({
                id: this.props.params.id,
                select: Solution,
                tenancy: appContext().tenantId
            });
            await this.setStateAsync({
                id: solutionData.Id,
                name: solutionData.Name,
                displayName: this.languageValuesHelper.fromString(solutionData.DisplayName),
                description: solutionData.Description,
                enabled: TypeHelper.toBoolean(solutionData.IsEnabled) ?? false,
                showOnHomePage: TypeHelper.toBoolean(solutionData.ShowOnHomePage) ?? false,
                showOnMenu: TypeHelper.toBoolean(solutionData.ShowOnMenu) ?? false,
                ontologyId: solutionData.Ontology_ID,
                imageUri: solutionData.ImageUri,
                iconUri: solutionData.IconUri,
                applicationLink: solutionData.ApplicationUri,
                order: solutionData.OrderSequence.toString(),
                concurrencyToken: solutionData["@odata.etag"],
            });
        }
        catch (error)
        {
            console.error(error)
        }
    }

    private async loadOntologies(): Promise<void>
    {
        try
        {
            const ontologies = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.get<PagedResponse<Ontology[]>>({
                select: Ontology,
                tenancy: appContext().tenantId,
                top: ApiConstants.largeODataTop,
            });
            this.setState({ ontologies: ontologies.value });
        }
        catch (error)
        {
            console.error(error)
        }
    }

    private async loadImages(): Promise<void>
    {
        const files = await appContext().webEntryApiClientV1.api.webentry.v1.file.byPath.get<IFile[]>({
            path: 'c/Solutions'
        });
        const images = files.map(file =>
        {
            const newItem = { ...file };
            newItem.path = `${Helper.getStorageUrl()}/images/c/Solutions/${file.name}`;
            return newItem;
        });
        this.setState({ images: images });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () =>
        {
            if (this.props.params.id)
            {
                await this.updateSolution();
            }
            else
            {
                await this.createSolution();
            }
        });
    }

    private async updateSolution(): Promise<void>
    {
        try
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.patch<IPatchSolutionResponse>({
                tenancy: appContext().tenantId,
                id: this.state.id,
                body: {
                    Name: this.state.name,
                    Description: this.state.description,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    IsEnabled: this.state.enabled ? 'Yes' : 'No',
                    ShowOnHomePage: this.state.showOnHomePage ? 'Yes' : 'No',
                    ShowOnMenu: this.state.showOnMenu ? 'Yes' : 'No',
                    ImageUri: this.state.imageUri,
                    IconUri: this.state.iconUri,
                    ApplicationUri: this.state.applicationLink,
                    OrderSequence: parseInt(this.state.order),
                    '@odata.etag': this.state.concurrencyToken
                }
            });
            this.props.navigate(`/admin/solutions`);
        } catch (error)
        {
            console.error(error)
        }
    }

    private async createSolution(): Promise<void>
    {
        try
        {
            const newSolution = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.post<IPostSolutionResponse>({
                tenancy: appContext().tenantId,
                body: {
                    Name: this.state.name,
                    Description: this.state.description,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    IsEnabled: this.state.enabled ? 'Yes' : 'No',
                    ShowOnHomePage: this.state.showOnHomePage ? 'Yes' : 'No',
                    ShowOnMenu: this.state.showOnMenu ? 'Yes' : 'No',
                    ImageUri: this.state.imageUri,
                    IconUri: this.state.iconUri,
                    ApplicationUri: this.state.applicationLink,
                    Ontology_ID: this.state.ontologyId,
                    OrderSequence: parseInt(this.state.order),
                }
            });
            const newSolutionResponse = newSolution.CreatedObject
            this.props.navigate(`/admin/solutions/${newSolutionResponse.Id}/edit`);
        } catch (error)
        {
            console.error(error)
        }
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.delete({
                id: this.props.params.id ?? '',
                tenancy: appContext().tenantId,
            });
            this.props.navigate(`/admin/solutions`);
        });
    }

    private handleInputChange<K extends keyof IState>(fieldName: K, value: IState[K]): void
    {
       this.setState({ [fieldName]: value } as Pick<IState, K>, () => this.validator.validate(fieldName)) 
    }

    private renderDetails(): JSX.Element
    {
        return (
            <EditSolutionDetailsForm
                id={this.state.id}
                ontologyName={this.state.ontologies.filter(x => x.Id === this.state.ontologyId)[0]?.Name ?? ''}
                ontologyId={this.state.ontologyId}
                ontologies={this.state.ontologies}
                name={this.state.name}
                displayName={this.state.displayName}
                ontologyError={this.state.ontologyError}
                nameError={this.state.nameError}
                description={this.state.description}
                descriptionError={this.state.descriptionError}
                enabled={this.state.enabled}
                showOnHomePage={this.state.showOnHomePage}
                showOnMenu={this.state.showOnMenu}
                images={this.state.images}
                imageUri={this.state.imageUri}
                iconUri={this.state.iconUri}
                applicationLink={this.state.applicationLink}
                isNew={this.state.id === ''}
                order={this.state.order}
                handleInputChange={(fieldName, e) => this.handleInputChange(fieldName, e)}
                loadImages={() => this.loadImages()}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
            />
        )
    }

    public render(): JSX.Element
    {
        return (
            <LayoutContainer>
                <LayoutContent>
                    <GridContainer
                        spacing={5}
                    >
                        <GridItem>
                            {this.renderDetails()}
                        </GridItem>
                        {
                            this.props.params.id &&
                            <>
                                <GridItem>
                                    <EditSolutionParameters
                                        solutionId={this.props.params.id ?? ''}
                                        ontologyId={this.state.ontologyId}
                                        onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
                                    />
                                </GridItem>
                                <GridItem>
                                    <EditSolutionPackages
                                        solutionId={this.props.params.id ?? ''}
                                        onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 2) }))}
                                        handleEditPackage={id => this.props.navigate(`/admin/packages/${id}/edit`)}
                                    />
                                </GridItem>
                            </>
                        }
                    </GridContainer>
                </LayoutContent>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                this.props.params.id &&
                                <DeleteButton
                                    disabled={!this.state.canDelete}
                                    dialogMessage={this.labels.Platform_Solution_ConfirmDeleteMsg}
                                    onDelete={() => this.handleDelete()}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.props.navigate(`/admin/solutions`)}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                disabled={!this.validator.isValid}
                                onClick={() => this.handleSave()}
                            >
                                {this.labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
            </LayoutContainer>
        );
    }
}

export default withRouting(EditSolution);

export interface IProps extends IRouterProps<IQueryParams>
{
}

export interface IState
{
    id: string;
    name: string;
    displayName: ILanguageValues;
    nameError: string;
    description: string;
    descriptionError: string;
    enabled: boolean;
    showOnHomePage: boolean;
    showOnMenu: boolean;
    ontologyId: string;
    ontologyError: string;
    concurrencyToken: string;
    ontologies: Ontology[];
    images: IFile[];
    imageUri: string;
    iconUri: string;
    applicationLink: string;
    order: string;
    canDelete: boolean;
    sections: IPageNavSection[];
}

export interface IQueryParams
{
    id: string | undefined;
}

export interface IFile
{
    name: string;
    path: string;
    lastModified: string;
    createdAt: string;
    size: string;
}

export type FieldName = 'Name' | 'Description' | 'DisplayName' | 'ApplicationLink' | 'Order' | 'ImageUri' | 'IconUri' | 'IsEnabled' | 'ShowOnHomePage' | 'ShowOnMenu' | 'Ontology';
